import React from "react";

/**
 * The regulatory label on the underside of a unit, next to the Ethernet jack, drawn as an SVG so
 * the wizard can point at exactly which of the several codes on it is the device key. The key is
 * the large seven-character code under the Omnisight mark; the HVIN, FCC ID and IC ID beside it
 * are not what the server wants and are easy to grab by mistake.
 *
 * Drawn rather than photographed so it stays crisp at any size and the key can be highlighted.
 * Always white-on-black regardless of theme: it is a picture of a sticker, not a UI surface.
 */
export const DeviceLabel: React.FC<{ className?: string; sampleKey?: string }> = ({
    className = "",
    sampleKey = "ABC1234",
}) => (
    <svg
        viewBox="0 0 510 200"
        role="img"
        aria-label={`Device label. The device key is the large code under the Omnisight logo, for example ${sampleKey}.`}
        className={`w-full max-w-[460px] ${className}`}
    >
        <rect x="0.5" y="0.5" width="509" height="199" rx="6" fill="#ffffff" stroke="#c9cfd6" />

        {/* Omnisight "O" mark — same path as the dashboard logo, scaled from its 69px artboard. */}
        <g transform="translate(76 12) scale(0.7)" fill="#0e141c">
            <path d="M56.9808436,58.0793392 C54.1807882,61.1940176 50.8458827,63.6794668 46.9761101,65.5356914 C43.1063644,67.3919072 38.9062557,68.320013 34.3758267,68.320013 C29.6566214,68.320013 25.3621317,67.3604453 21.4923882,65.4413058 C17.6226305,63.5221567 14.3034529,60.9737743 11.5348498,57.7961824 C9.42462726,55.3742233 3.24917246,45.5962268 0.431417958,39.1881052 L11.8259346,39.1892075 C12.1009075,40.8905143 12.5229798,42.5624009 13.0921521,44.2048678 C14.1933006,47.3824766 15.75064,50.1982718 17.76417,52.6522524 C19.7777005,55.1062456 22.200228,57.0568462 25.0317544,58.5040653 C27.8632777,59.9512955 31.0094164,60.67491 34.4701721,60.67491 C38.0567714,60.67491 41.2658314,59.9041074 44.0973587,58.3624942 C46.928887,56.820889 49.3356816,54.8073628 51.3177545,52.3219095 C53.2998181,49.8364551 54.8099695,47.0206636 55.8481949,43.8745212 C56.3610355,42.3204454 56.7472182,40.7586928 57.0067386,39.1892632 L68.5605209,39.1878457 C65.6432986,45.7187927 59.1289243,55.6898985 56.9808436,58.0793392 Z M34.5645958,0.363403 C39.2837989,0.363403 43.5625508,1.3544364 47.400842,3.33650333 C51.2391403,5.31857339 54.5425829,7.91414009 57.3111995,11.1232014 C59.4254843,13.5738659 65.7105263,23.4560291 68.56314,29.8676565 L57.0778619,29.8670659 C56.8058559,28.0370336 56.364504,26.2408696 55.7538079,24.4785755 C54.6526517,21.3009782 53.0795855,18.5009142 51.0345979,16.0783853 C48.9896011,13.655859 46.5670791,11.7209838 43.7670181,10.2737593 C40.9669497,8.82653901 37.8680033,8.10292 34.4701721,8.10292 C30.8835713,8.10292 27.6745086,8.85799201 24.8429859,10.3681405 C22.011459,11.8782889 19.6046616,13.8603569 17.6225952,16.3143441 C15.6405255,18.7683379 14.114647,21.568402 13.0449594,24.7145354 C12.4672615,26.4136468 12.0455606,28.13111 11.7798559,29.8669256 L0.382294034,29.8677253 C3.24361919,23.272613 9.73041309,13.0756907 11.9123845,10.6512774 C14.743912,7.50514255 18.0945518,5.00396214 21.9643039,3.14773604 C25.8340524,1.29151401 30.0341547,0.363403 34.5645958,0.363403 Z M27.0000004,34.5 C27.0000004,30.3578641 30.3578645,27 34.5000004,27 C38.6421358,27 42.0000004,30.3578641 42.0000004,34.5 C42.0000004,38.6421354 38.6421358,42 34.5000004,42 C30.3578645,42 27.0000004,38.6421354 27.0000004,34.5 Z" />
        </g>

        {/* The device key — the one thing on the label the wizard asks for. */}
        <rect x="16" y="70" width="176" height="48" rx="8" fill="none" stroke="var(--accent, #f47a20)" strokeWidth="3" />
        <text
            x="104"
            y="106"
            textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui, Helvetica, Arial, sans-serif"
            fontWeight="800"
            fontSize="34"
            letterSpacing="1"
            fill="#0e141c"
        >
            {sampleKey}
        </text>

        <line x1="204" y1="14" x2="204" y2="122" stroke="#0e141c" strokeWidth="2" />

        <g fontFamily="ui-sans-serif, system-ui, Helvetica, Arial, sans-serif" fontSize="19" fill="#0e141c">
            <text x="218" y="44">
                <tspan fontWeight="700">HVIN: </tspan>
                <tspan>MEGA-V5</tspan>
            </text>
            <text x="218" y="79">
                <tspan fontWeight="700">FCC ID: </tspan>
                <tspan>2A6OYMEGA1</tspan>
            </text>
            <text x="218" y="114">
                <tspan fontWeight="700">IC ID: </tspan>
                <tspan>28582-MEGA1</tspan>
            </text>
        </g>

        <g
            fontFamily="ui-sans-serif, system-ui, Helvetica, Arial, sans-serif"
            fontWeight="700"
            fontSize="10.5"
            fill="#0e141c"
            textAnchor="middle"
        >
            <text x="255" y="140">Contains FCC ID: PD98265NG, ISED: 1000M-8265NG.</text>
            <text x="255" y="154">This device complies with part 15 of the FCC Rules. Operation is subject</text>
            <text x="255" y="168">to the following two conditions: (1) this device may not cause harmful</text>
            <text x="255" y="182">interference, and (2) this device must accept any interference received,</text>
            <text x="255" y="196">including interference that may cause undesired operation</text>
        </g>
    </svg>
);
